import { type Filtres, filtresSchema } from '@carte/contrats/filtres';
import { RAYON_KM_DEFAUT, RAYON_KM_MAX, RAYON_KM_MIN } from '@carte/contrats/rayon';
import { estActif, type StatutCode } from '@carte/domaine/statut';
import { z } from 'zod';

/**
 * Paramètres d'URL de l'écran carte (SPEC 4, règle 2) : les filtres partagés avec l'API, la vue
 * (carte ou liste) et le cadrage de la carte. Copier l'URL reproduit l'écran.
 */
export const VUES = ['carte', 'liste'] as const;
export type Vue = (typeof VUES)[number];

/** Lectures du mode Territoire (absent : points et amas, l'affichage habituel). */
export const LECTURES_TERRITOIRE = ['densite', 'mentors', 'departements'] as const;
export type LectureTerritoire = (typeof LECTURES_TERRITOIRE)[number];

/**
 * Un paramètre illisible (lien tronqué, valeur saisie à la main) retombe sur sa valeur par défaut au lieu
 * de faire échouer tout l'écran : chaque champ porte son `.catch`.
 */
const filtres = filtresSchema.shape;

export const carteRechercheSchema = filtresSchema.extend({
  mentor: filtres.mentor.catch(undefined),
  consultant: filtres.consultant.catch(undefined),
  statut: filtres.statut.catch(undefined),
  type: filtres.type.catch(undefined),
  pays: filtres.pays.catch(undefined),
  dep: filtres.dep.catch(undefined),
  precision: filtres.precision.catch(undefined),
  actifs: filtres.actifs.catch(true),
  retard: filtres.retard.catch(false),
  bbox: filtres.bbox.catch(undefined),
  vue: z.enum(VUES).default('carte').catch('carte'),
  /** `lon,lat,zoom`, par exemple `-1.4300,46.6700,9.0`. */
  cadrage: z.string().optional().catch(undefined),
  /** Centre du périmètre posé sur la carte, `lon,lat` (absent : pas de périmètre). */
  autour: z.string().optional().catch(undefined),
  /** Zone de la carte au passage en liste, `ouest,sud,est,nord` (n'existe qu'en vue liste). */
  zone: z.string().optional().catch(undefined),
  /** Rayon du périmètre en km. */
  rayon: z.coerce
    .number()
    .min(RAYON_KM_MIN)
    .max(RAYON_KM_MAX)
    .default(RAYON_KM_DEFAUT)
    .catch(RAYON_KM_DEFAUT),
  /** Lecture du mode Territoire : ce n'est pas un filtre (ni API, ni badge, ni réinitialisation). */
  territoire: z.enum(LECTURES_TERRITOIRE).optional().catch(undefined),
});
export type CarteRecherche = z.infer<typeof carteRechercheSchema>;

export interface Cadrage {
  lon: number;
  lat: number;
  zoom: number;
}

/** Lit un cadrage `lon,lat,zoom` ; renvoie null si la valeur est absente ou invalide. */
export function lireCadrage(texte: string | undefined): Cadrage | null {
  if (!texte) return null;
  const morceaux = texte.split(',').map(Number);
  if (morceaux.length !== 3 || morceaux.some((n) => !Number.isFinite(n))) return null;
  const [lon, lat, zoom] = morceaux as [number, number, number];
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90 || zoom < 0 || zoom > 24) return null;
  return { lon, lat, zoom };
}

export function ecrireCadrage({ lon, lat, zoom }: Cadrage): string {
  return `${lon.toFixed(4)},${lat.toFixed(4)},${zoom.toFixed(1)}`;
}

/** Recherche d'URL sans les valeurs par défaut, pour des adresses courtes et lisibles. */
export function rechercheVersUrl(r: CarteRecherche): Record<string, unknown> {
  const sortie: Record<string, unknown> = {};
  if (r.mentor) sortie.mentor = r.mentor;
  if (r.consultant) sortie.consultant = r.consultant;
  for (const cle of ['statut', 'type', 'pays', 'dep', 'precision'] as const) {
    const valeurs = r[cle];
    if (valeurs && valeurs.length > 0) sortie[cle] = valeurs;
  }
  if (!r.actifs) sortie.actifs = false;
  if (r.retard) sortie.retard = true;
  if (r.bbox) sortie.bbox = r.bbox;
  if (r.vue !== 'carte') sortie.vue = r.vue;
  if (r.zone && r.vue === 'liste') sortie.zone = r.zone;
  if (r.cadrage) sortie.cadrage = r.cadrage;
  if (r.autour) {
    sortie.autour = r.autour;
    if (r.rayon !== RAYON_KM_DEFAUT) sortie.rayon = r.rayon;
  }
  if (r.territoire) sortie.territoire = r.territoire;
  return sortie;
}

/** Les filtres seuls (sans la vue, le cadrage, le périmètre ni la lecture Territoire), pour les appels à l'API. */
export function extraireFiltres(r: CarteRecherche): Filtres {
  const {
    vue: _vue,
    cadrage: _cadrage,
    zone: _zone,
    autour: _autour,
    rayon: _rayon,
    territoire: _territoire,
    ...filtres
  } = r;
  return filtres;
}

/** Nombre de filtres différents de leur valeur par défaut (badge du bouton "Filtres"). */
export function compterFiltres(f: Filtres): number {
  let n = 0;
  if (f.mentor) n++;
  if (f.consultant) n++;
  for (const cle of ['statut', 'type', 'pays', 'dep', 'precision'] as const) {
    if (f[cle]?.length) n++;
  }
  if (!f.actifs) n++;
  if (f.retard) n++;
  if (f.bbox) n++;
  return n;
}

/**
 * Nouvelle sélection de statuts. Choisir un statut qui n'est pas actif (Clos, Arrêté) désactive
 * "Actifs seulement", sinon la sélection ne donnerait jamais aucun client.
 */
export function appliquerStatuts(filtres: Filtres, statuts: StatutCode[]): Partial<Filtres> {
  const demandeInactif = statuts.some((s) => !estActif(s));
  return {
    statut: statuts.length > 0 ? statuts : undefined,
    ...(demandeInactif && filtres.actifs ? { actifs: false } : {}),
  };
}

/** Ajoute ou retire une valeur d'une liste de filtre. */
export function basculerValeur<T extends string>(liste: readonly T[] | undefined, valeur: T): T[] {
  const courante = liste ?? [];
  return courante.includes(valeur) ? courante.filter((v) => v !== valeur) : [...courante, valeur];
}
